import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { overrideRequestsApi, type OverrideRequestDto } from '@/lib/api/override-requests.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

function relativeAge(iso: string): string {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

function expiresIn(iso: string): string {
  const sec = Math.ceil((new Date(iso).getTime() - Date.now()) / 1000);
  if (sec <= 0) return 'Expired';
  return `${sec}s`;
}

function actionTitle(action: string): string {
  return action
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function RequestCard({
  row,
  expanded,
  onSelect,
}: {
  row: OverrideRequestDto;
  expanded: boolean;
  onSelect: () => void;
}) {
  const queryClient = useQueryClient();
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');

  const approveMut = useMutation({
    mutationFn: () => overrideRequestsApi.approve(row.id),
    onSuccess: () => {
      toastSuccess('Approved');
      void queryClient.invalidateQueries({ queryKey: ['override-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['override-pending-count'] });
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      toastError(parsed.message);
      void queryClient.invalidateQueries({ queryKey: ['override-requests'] });
    },
  });

  const denyMut = useMutation({
    mutationFn: () => overrideRequestsApi.deny(row.id, denyReason.trim()),
    onSuccess: () => {
      toastSuccess('Denied');
      setDenyOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['override-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['override-pending-count'] });
    },
    onError: (err) => toastError(parseApiError(err).message),
  });

  const ctx = row.context ?? {};
  const original = typeof ctx.originalPrice === 'number' ? ctx.originalPrice : null;
  const requested = typeof ctx.requestedPrice === 'number' ? ctx.requestedPrice : null;
  const amount = typeof ctx.amount === 'number' ? ctx.amount : null;
  const percent = typeof ctx.percent === 'number' ? ctx.percent : null;
  const ticketNumber = typeof ctx.ticketNumber === 'string' ? ctx.ticketNumber : null;
  const customerName = typeof ctx.customerName === 'string' ? ctx.customerName : null;
  const itemName = typeof ctx.itemName === 'string' ? ctx.itemName : null;

  return (
    <div
      className={cn(
        'rounded-2xl border bg-white p-5 shadow-sm',
        expanded ? 'border-primary ring-1 ring-primary/20' : 'border-slate-200',
      )}
    >
      <button type="button" className="w-full text-left" onClick={onSelect}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-bold text-slate-950">
              {actionTitle(row.action)} Approval Request
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Requested by {row.requestedByName} · {relativeAge(row.createdAt)}
            </p>
          </div>
          {row.status === 'pending' ? (
            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">
              Expires in {expiresIn(row.expiresAt)}
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-700">
              {row.status}
            </span>
          )}
        </div>
      </button>

      {expanded ? (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4 text-sm">
          {(ticketNumber || customerName) && (
            <p className="font-semibold text-slate-900">
              {[ticketNumber, customerName].filter(Boolean).join(' · ')}
            </p>
          )}
          {itemName ? <p className="text-slate-700">{itemName}</p> : null}
          {(original != null || requested != null || amount != null) && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-700">
              {original != null ? (
                <>
                  <dt>Original</dt>
                  <dd className="text-right font-medium">{formatCurrency(original)}</dd>
                </>
              ) : null}
              {requested != null ? (
                <>
                  <dt>Requested</dt>
                  <dd className="text-right font-medium">{formatCurrency(requested)}</dd>
                </>
              ) : null}
              {percent != null || amount != null ? (
                <>
                  <dt>Discount</dt>
                  <dd className="text-right font-medium">
                    {percent != null ? `${percent}%` : ''}
                    {percent != null && amount != null ? ' ' : ''}
                    {amount != null ? `(${formatCurrency(amount)})` : ''}
                  </dd>
                </>
              ) : null}
            </dl>
          )}
          {row.reason ? (
            <p className="rounded-xl bg-slate-50 px-3 py-2 text-slate-800">
              <span className="font-semibold">Reason: </span>
              {row.reason}
            </p>
          ) : null}
          {row.requesterOverrideCount7d > 0 ? (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-900">
              ⚠ {row.requestedByName.split(' ')[0]} has requested {row.requesterOverrideCount7d}{' '}
              overrides in the last 7 days
            </p>
          ) : null}
          {row.ticketId ? (
            <Link className="text-primary hover:underline" to={`/tickets/${row.ticketId}`}>
              Open ticket →
            </Link>
          ) : null}

          {row.status === 'pending' ? (
            denyOpen ? (
              <div className="space-y-2 pt-2">
                <textarea
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Denial reason (required)"
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 rounded-xl border border-slate-300 px-3 py-2 font-semibold"
                    onClick={() => setDenyOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="flex-1 rounded-xl bg-red-600 px-3 py-2 font-semibold text-white disabled:opacity-50"
                    disabled={!denyReason.trim() || denyMut.isPending}
                    onClick={() => denyMut.mutate()}
                  >
                    Confirm Deny
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  className="flex-1 rounded-xl border border-red-300 px-3 py-2 font-semibold text-red-700"
                  onClick={() => setDenyOpen(true)}
                >
                  Deny
                </button>
                <button
                  type="button"
                  className="flex-1 rounded-xl bg-primary px-3 py-2 font-semibold text-white disabled:opacity-50"
                  disabled={approveMut.isPending}
                  onClick={() => approveMut.mutate()}
                >
                  Approve
                </button>
              </div>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ApprovalsPage() {
  const [params] = useSearchParams();
  const focusId = params.get('id');
  const [selectedId, setSelectedId] = useState<string | null>(focusId);

  const listQuery = useQuery({
    queryKey: ['override-requests', 'pending'],
    queryFn: () => overrideRequestsApi.list({ status: 'pending' }),
    refetchInterval: 5_000,
  });

  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-950">Approvals</h1>
        <p className="mt-1 text-sm text-slate-500">
          Remote override requests waiting for a manager.
        </p>
      </div>

      {listQuery.isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          No pending approval requests
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <RequestCard
              key={row.id}
              row={row}
              expanded={selectedId === row.id || (!selectedId && row.id === focusId)}
              onSelect={() => setSelectedId(row.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
